<template>
	<form @submit.prevent="requestQuote" class="has-text-left">
		<transition name="slide">
			<div v-if="!sent" key="form">
				<h2 class="title is-5">Send this quote to my email</h2>
				<div class="field is-grouped">
					<div class="control is-expanded">
						<input
							v-model="email"
							type="email"
							ref="email"
							class="input"
							:class="{ 'is-red': error }"
							placeholder="Enter your work email"
							required="true"
							aria-label="Work email"
						/>
					</div>
					<div class="control">
						<button
							:disabled="email === ''"
							type="submit"
							class="button is-outlined"
							:class="{ 'is-grey': email === '', 'is-blue': email !== '' }"
						>Send quote</button>
					</div>
				</div>
			</div>
			<div v-else key="success">
				<p class="title is-4">
					<span class="icon-text">
						<span class="icon">
							<img src="/images/pricing/tick-green.svg" alt="Success" />
						</span>
						<span>Quote sent to <span v-text="email"></span>!</span>
					</span>
				</p>
			</div>
		</transition>
	</form>
</template>

<script>

import axios from 'axios';
import errorHandler from 'Utils/errorHandler.js';

export default {
	name: 'send-quote',

	data() {
		return {
			/* Email address for sending the quote */
			email: '',

			/* Whether the form has been submitted */
			sent: false,

			/* Whether there was an error sending the form */
			error: false,
		};
	},

	computed: {
		data() {
			return this.$store.data || {};
		},

		selection() {
			return this.$store.selection || {};
		},

		/* Array of products for the quote */
		productList() {
			const list = [];

			/* Compare & Deploy */
			if (this.selection.compare.users > 0 && this.selection.compare.tier !== '') {
				/* Add product code and user count */
				list.push({
					productCode: this.data.compare.products[this.selection.compare.tier].productCode,
					count: this.selection.compare.users,
				});
			}

			/* Automation */
			if (this.selection.compare.users > 0 && this.selection.automation.tier !== '') {
				/* Add product code */
				list.push({
					productCode: this.data.automation.products[this.selection.automation.tier].productCode,
					count: 1,
				});
			}

			/* Backup */
			if (this.selection.backup.users > 0 && this.selection.backup.tier !== '') {
				/* Add product code and user count */
				list.push({
					productCode: this.data.backup.products[this.selection.backup.tier].productCode,
					count: this.selection.backup.users,
				});
			}

			/* Code reviews */
			if (this.selection.codeReviews.users > 0 && this.selection.codeReviews.tier !== '') {
				/* Add product code and user count */
				list.push({
					productCode: this.data.codeReviews.products[this.selection.codeReviews.tier].productCode,
					count: this.selection.codeReviews.users,
				});
			}

			/* Addons */
			Object.keys(this.selection.addons).forEach(addon => {
				if (this.selection.addons[addon]) {
					/* Add product code */
					list.push({
						productCode: this.data.addons.products[addon].productCode,
						count: 1,
					});
				}
			});

			return list;
		},
	},

	methods: {
		/* Send quote request to backend */
		requestQuote() {
			axios.post(`${window.apiHost}/v2/quote/request`, {
				quoteData: {
					email: this.email,
					products: this.productList,
				},
				summary: {
					totalCost: this.selection.total / 100,
				},
			})
				.then(() => {
					this.sent = true;
					this.error = false;

					setTimeout(() => {
						this.sent = false;
					}, 2000);
				})
				.catch(error => {
					this.error = true;
					this.$refs.email.focus();

					errorHandler(error);
				});
		},
	},
};

</script>

<style lang="scss" scoped>
@import '@gearset/web-core/styles/includes/variables';

form {
	height: 100%;
	width: 100%;
	position: relative;

	& > * {
		height: 100%;
		width: 100%;
		display: flex;
		flex-direction: column;
		justify-content: center;
	}

	button {
		transition: color .2s ease-in-out, border .2s ease-in-out, opacity .2s ease-in-out;
	}
}


/* Positive feedback */

.slide-enter-active,
.slide-leave-active {
	transition: all .6s;

	.title,
	.button {
		transition: all 1.2s;
	}
}

.slide-enter-active {
	transition-timing-function: cubic-bezier(.33, 1, .68, 1);
	transition-delay: .55s;
	position: absolute;
	top: 0;

	.title {
		transition-timing-function: cubic-bezier(.33, 1, .68, 1);
		transition-delay: .5s;
	}

	.button {
		transition-timing-function: cubic-bezier(.33, 1, .68, 1);
		transition-delay: .25s;
	}
}

.slide-leave-active {
	transition-timing-function: cubic-bezier(.32, 0, .67, 0);
}

.slide-enter {
	opacity: 0;
	transform: translateX(-50px);
}

.slide-leave-to {
	opacity: 0;
	transform: translateX(50px);

	.title {
		transform: translateX(30px);
	}

	.button {
		transform: translateX(80px);
	}
}


/* Negative feedback */

.input.is-red {
	animation: error-shake .4s 1 linear;
}

@keyframes error-shake {
	0% { transform: translate(25px); }
	20% { transform: translate(-25px); }
	40% { transform: translate(12px); }
	60% { transform: translate(-12px); }
	80% { transform: translate(6px); }
	100% { transform: translate(0); }
}

</style>
